'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
	AlertCircle,
	CalendarDays,
	Check,
	ChevronsLeft,
	ChevronsRight,
	ChevronLeft,
	ChevronRight,
	CreditCard,
	Download,
	Info,
	Loader2,
	Clock,
	RefreshCw,
	Smartphone,
	WalletCards,
	X,
	Zap,
} from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { useAccountBalance, useBillOverview, useRechargeSummary, useTransactionList, useModelData, invalidateBalanceCache, invalidateRechargeCache } from '@/lib/swr/index.js';
import { createRecharge, type RechargePayWay, type RechargeResp } from '@/lib/api/wallet';
import { cancelOrder, fetchPayStatus } from '@/lib/community/community-api';

type AccountTab = 'balance' | 'bill' | 'recharge';
type BillTab = 'usage' | 'monthly';
type PayMethod = 'alipay' | 'wechat';

const POLL_INTERVAL = 3000;
const MAX_POLL_COUNT = 60;

/** 金额（分）转元显示 */
function formatFen(value: number): string {
	return (value / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const MENU_ITEMS: Array<{ value: AccountTab; label: string }> = [
	{ value: 'balance', label: '账户余额' },
	{ value: 'bill', label: '账单明细' },
	{ value: 'recharge', label: '充值明细' },
];

const RECHARGE_AMOUNTS = [50, 100, 200, 500, 1000, 2000, 3000];

function getRechargePayWay(method: PayMethod): RechargePayWay {
	return method === 'alipay' ? 'ALI_OC' : 'WX_NATIVE';
}

function formatCountdown(seconds: number) {
	const m = Math.floor(seconds / 60).toString().padStart(2, '0');
	const s = (seconds % 60).toString().padStart(2, '0');
	return { m, s };
}

export function AccountCenterPage() {
	const [activeTab, setActiveTab] = useState<AccountTab>('balance');

	return (
		<div className="min-h-dvh bg-background pt-14 text-foreground">
			<div className="mx-auto grid max-w-7xl gap-5 px-4 py-6 sm:px-6 lg:grid-cols-[100px_minmax(0,1fr)]">
				<aside className="liquid-glass-card no-hover h-fit rounded-2xl p-2">
					{MENU_ITEMS.map((item) => (
						<button
							className={`h-10 w-full rounded-xl px-3 text-left text-sm transition-colors ${activeTab === item.value ? 'bg-primary/12 font-medium text-primary' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
								}`}
							key={item.value}
							onClick={() => setActiveTab(item.value)}
							type="button"
						>
							{item.label}
						</button>
					))}
				</aside>

				<main className="min-w-0 space-y-5">
					{activeTab === 'balance' && <BalancePanel />}
					{activeTab === 'bill' && <BillPanel />}
					{activeTab === 'recharge' && <RechargePanel />}
				</main>
			</div>
		</div>
	);
}

function _OverviewCards() {
	const { data: balance } = useAccountBalance();
	const totalBalance = balance ? formatFen(balance.totalBalance) : '-';
	const cashBalance = balance ? formatFen(balance.cashBalance) : '0.00';
	const giftedBalance = balance ? formatFen(balance.giftedBalance) : '0.00';
	const totalConsumed = balance ? formatFen(balance.totalConsumed) : '-';

	return (
		<section className="grid gap-3 md:grid-cols-2">
			<OverviewCard
				hint={`现金 ¥${cashBalance} + 赠送 ¥${giftedBalance}`}
				icon={<WalletCards className="h-4 w-4" />}
				label="账户可用余额"
				value={`¥${totalBalance}`}
			/>
			<OverviewCard hint="" icon={<Zap className="h-4 w-4" />} label="累计消费" value={`¥${totalConsumed}`} />
		</section>
	);
}

function OverviewCard({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint: string }) {
	return (
		<div className="liquid-glass-card no-hover rounded-2xl p-5">
			<div className="flex items-center justify-between">
				<span className="text-sm text-muted-foreground">{label}</span>
				<span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">{icon}</span>
			</div>
			<p className="mt-3 text-2xl font-bold tracking-normal">{value}</p>
			<p className="mt-1 text-xs text-muted-foreground">{hint}</p>
		</div>
	);
}

function BalancePanel() {
	const toast = useToast();
	const { data: balance, isLoading: balanceLoading } = useAccountBalance();
	const [amount, setAmount] = useState<number | 'custom'>(50);
	const [customAmount, setCustomAmount] = useState('');
	const [payMethod, setPayMethod] = useState<PayMethod>('alipay');
	const [agreed, setAgreed] = useState(false);
	const [warningOpen, setWarningOpen] = useState(false);
	const [warningEnabled, setWarningEnabled] = useState(false);
	const [warningValue, setWarningValue] = useState('');
	const [cashierOrder, setCashierOrder] = useState<RechargeResp | null>(null);
	const [cashierAmount, setCashierAmount] = useState(0);
	const [cashierMethod, setCashierMethod] = useState<PayMethod>('alipay');
	const [rechargeLoading, setRechargeLoading] = useState(false);

	const rechargeAmount = amount === 'custom' ? Number(customAmount || 0) : amount;

	const totalBalance = balance ? formatFen(balance.totalBalance) : '-';
	const cashBalance = balance ? formatFen(balance.cashBalance) : '0.00';
	const giftedBalance = balance ? formatFen(balance.giftedBalance) : '0.00';

	const handleCreateRecharge = useCallback(async () => {
		const amountFen = Math.round(rechargeAmount * 100);
		if (amountFen < 100 || amountFen > 500000) {
			toast.error('充值金额需在 1 元到 5000 元之间');
			return;
		}
		setRechargeLoading(true);
		try {
			const resp = await createRecharge({
				amount: amountFen,
				payWay: getRechargePayWay(payMethod),
			});
			setCashierOrder(resp);
			setCashierAmount(amountFen);
			setCashierMethod(payMethod);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : '发起充值失败，请重试');
		} finally {
			setRechargeLoading(false);
		}
	}, [payMethod, rechargeAmount, toast]);

	if (balanceLoading) return <BalanceSkeleton />;

	return (
		<>
			<section className="grid gap-4 xl:grid-cols-2">
				<div className="liquid-glass-card no-hover rounded-2xl p-5">
					<div className="flex items-center justify-between gap-3">
						<div>
							<h2 className="text-base font-semibold">余额</h2>
							<p className="mt-1 text-xs text-muted-foreground">余额准实时更新，数据将在次日 7:00（UTC 时间）完成最终核对。</p>
						</div>
						{/* <button
							className="shrink-0 whitespace-nowrap rounded-full bg-secondary/70 px-4 py-2 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-secondary/70 disabled:hover:text-muted-foreground"
							disabled={!balance || balance.cashBalance <= 0}
							type="button"
						>
							申请退款
						</button> */}
					</div>
					<div className="mt-5 rounded-xl bg-secondary/45 p-4">
						<p className="text-xs">账户余额</p>
						<p className="mt-3 text-3xl font-semibold tracking-normal">¥ {totalBalance}</p>
						<p className="mt-4 text-xs text-muted-foreground">
							现金余额：¥ {cashBalance} <span className="mx-2 text-border">|</span> 赠送余额：¥ {giftedBalance}
						</p>
					</div>
				</div>

				<div className="liquid-glass-card no-hover rounded-2xl p-5">
					<div className="flex items-start justify-between gap-3">
						<div>
							<h2 className="text-base font-semibold">余额预警</h2>
							<p className="mt-1 text-xs text-muted-foreground">低于阈值时向注册手机号/邮箱发送通知。</p>
						</div>
						<button
							className="rounded-full bg-primary/10 px-4 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/15"
							onClick={() => toast.info('功能暂未上线')}
							type="button"
						>
							设置
						</button>
					</div>
					<div className="mt-5 rounded-xl bg-secondary/45 p-4">
						<p className="text-xs">预警阈值</p>
						<p className="mt-3 text-3xl font-semibold tracking-normal">{warningEnabled && warningValue ? `¥ ${warningValue}` : '¥ -'}</p>
						<p className={`mt-4 text-xs ${warningEnabled ? 'text-primary' : 'text-amber-600 dark:text-amber-300'}`}>
							{warningEnabled ? '余额预警已开启' : '余额预警未开启'}
						</p>
					</div>
				</div>
			</section>

			<section className="liquid-glass-card no-hover rounded-2xl p-6">
				<h2 className="text-center text-lg font-semibold">在线充值</h2>
				<div className="mx-auto mt-5 max-w-3xl">
					<p className="mb-2 text-xs text-muted-foreground">充值金额</p>
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
						{RECHARGE_AMOUNTS.map((item) => (
							<SelectButton active={amount === item} key={item} onClick={() => setAmount(item)}>
								¥ {item}
							</SelectButton>
						))}
						<SelectButton active={amount === 'custom'} onClick={() => setAmount('custom')}>
							自定义
						</SelectButton>
					</div>

					{amount === 'custom' && (
						<div className="mt-3 flex h-10 overflow-hidden rounded-xl border border-border/70 bg-background">
							<input
								className="min-w-0 flex-1 bg-transparent px-3 text-sm outline-none"
								min={1}
								onChange={(event) => setCustomAmount(event.target.value)}
								placeholder="请输入自定义充值金额"
								type="number"
								value={customAmount}
							/>
							<span className="flex w-12 items-center justify-center border-l border-border/70 text-sm">¥</span>
						</div>
					)}

					<p className="mb-2 mt-5 text-xs text-muted-foreground">支付方式</p>
					<div className="grid gap-2 sm:grid-cols-2">
						<PayButton active={payMethod === 'alipay'} icon={<CreditCard className="h-4 w-4" />} label="支付宝" onClick={() => setPayMethod('alipay')} />
						<PayButton
							active={payMethod === 'wechat'}
							icon={<Smartphone className="h-4 w-4 text-[#16a34a]" />}
							label="微信支付"
							onClick={() => setPayMethod('wechat')}
						/>
					</div>

					<label className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
						<input checked={agreed} className="h-3.5 w-3.5 rounded border-border" onChange={(event) => setAgreed(event.target.checked)} type="checkbox" />
						已阅读并同意遵守 <span className="text-primary">服务协议</span>
					</label>

					<button
						className="mt-4 flex h-10 w-full items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground transition-opacity hover:bg-primary/90 disabled:opacity-50"
						disabled={!agreed || rechargeAmount <= 0 || rechargeLoading}
						onClick={handleCreateRecharge}
						type="button"
					>
						{rechargeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : '立即充值'}
					</button>
				</div>
			</section>

			<BalanceWarningModal
				enabled={warningEnabled}
				onClose={() => setWarningOpen(false)}
				onConfirm={(nextEnabled, nextValue) => {
					setWarningEnabled(nextEnabled);
					setWarningValue(nextValue);
					setWarningOpen(false);
					toast.success('余额预警设置成功');
				}}
				open={warningOpen}
				value={warningValue}
			/>
			{cashierOrder && (
				<RechargeCashierModal
					amount={cashierAmount}
					method={cashierMethod}
					onClose={() => setCashierOrder(null)}
					onSuccess={async () => {
						setCashierOrder(null);
						toast.success('充值成功');
						await Promise.all([invalidateBalanceCache(), invalidateRechargeCache()]);
					}}
					order={cashierOrder}
				/>
			)}
		</>
	);
}

function RechargeCashierModal({
	order,
	amount,
	method,
	onClose,
	onSuccess,
}: {
	order: RechargeResp;
	amount: number;
	method: PayMethod;
	onClose: () => void;
	onSuccess: () => void | Promise<void>;
}) {
	const [qrDataUrl, setQrDataUrl] = useState('');
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [countdown, setCountdown] = useState(0);
	const pollCountRef = useRef(0);
	const settledRef = useRef(false);

	useEffect(() => {
		let cancelled = false;
		async function buildQr() {
			setLoading(true);
			setError('');
			try {
				if (!order.payData) throw new Error('未获取到支付参数');
				const nextQr =
					order.payDataType === 'codeImgUrl'
						? order.payData
						: await QRCode.toDataURL(order.payData, {
							width: 180,
							margin: 1,
							color: { dark: '#000000', light: '#ffffff' },
						});
				if (!cancelled) setQrDataUrl(nextQr);
			} catch (err) {
				if (!cancelled) setError(err instanceof Error ? err.message : '获取支付二维码失败');
			} finally {
				if (!cancelled) setLoading(false);
			}
		}

		const expiry = new Date(order.expiredAt).getTime();
		setCountdown(Math.max(0, Math.floor((expiry - Date.now()) / 1000)));
		void buildQr();
		return () => {
			cancelled = true;
		};
	}, [order]);

	useEffect(() => {
		if (countdown <= 0) return;
		const timer = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					setError('支付已超时，请重新发起充值');
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(timer);
	}, [countdown]);

	useEffect(() => {
		const timer = setInterval(async () => {
			if (settledRef.current) return;
			pollCountRef.current += 1;
			if (pollCountRef.current > MAX_POLL_COUNT) {
				clearInterval(timer);
				setError('支付确认超时，请稍后查看账户余额');
				return;
			}
			try {
				const status = await fetchPayStatus(order.orderNo);
				if (status.payStatus === 2) {
					settledRef.current = true;
					clearInterval(timer);
					await onSuccess();
				} else if (status.payStatus === 3) {
					clearInterval(timer);
					setError('订单已关闭，请重新发起充值');
				}
			} catch {
				// Ignore transient polling failures.
			}
		}, POLL_INTERVAL);
		return () => clearInterval(timer);
	}, [onSuccess, order.orderNo]);

	const { m, s } = formatCountdown(countdown);
	const methodLabel = method === 'alipay' ? '支付宝' : '微信支付';

	async function handleClose() {
		if (!settledRef.current) {
			try {
				await cancelOrder(order.orderNo, '用户取消');
			} catch {
				// Closing the cashier should not be blocked by a cancel failure.
			}
		}
		onClose();
	}

	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center liquid-glass-overlay px-4">
			<div className="liquid-glass-modal w-full max-w-md rounded-2xl">
				<div className="flex items-center justify-between border-b border-border px-6 py-4">
					<div>
						<h2 className="text-base font-semibold">收银台</h2>
						<p className="mt-1 text-xs text-muted-foreground">{methodLabel}充值</p>
					</div>
					<button aria-label="关闭收银台" className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary" onClick={handleClose} type="button">
						<X className="h-4 w-4" />
					</button>
				</div>

				<div className="px-6 py-6 text-center">
					<p className="text-3xl font-semibold">¥ {formatFen(amount)}</p>
					<p className="mt-1 text-xs text-muted-foreground">订单编号：{order.orderNo}</p>
					<div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
						<Clock className="h-3.5 w-3.5" />
						<span>支付剩余 {m}:{s}</span>
					</div>

					<div className="mt-6 flex min-h-[190px] items-center justify-center">
						{loading ? (
							<div className="flex flex-col items-center gap-3 text-muted-foreground">
								<Loader2 className="h-8 w-8 animate-spin" />
								<p className="text-xs">正在获取支付二维码</p>
							</div>
						) : error ? (
							<div className="flex flex-col items-center gap-3 text-red-500">
								<AlertCircle className="h-8 w-8" />
								<p className="text-sm">{error}</p>
							</div>
						) : (
							<div className="flex flex-col items-center gap-3">
								<div className="rounded-xl bg-white p-2 shadow-sm ring-1 ring-border">
									<img alt="支付二维码" className="block" height={160} src={qrDataUrl} width={160} />
								</div>
								<p className="text-xs text-muted-foreground">请使用{methodLabel}扫码完成支付</p>
							</div>
						)}
					</div>
				</div>

				<div className="border-t border-border px-6 py-4">
					<button className="flex h-10 w-full items-center justify-center gap-2 rounded-full bg-secondary text-sm font-medium hover:bg-secondary/80" onClick={handleClose} type="button">
						<RefreshCw className="h-4 w-4" />
						取消并重新选择
					</button>
				</div>
			</div>
		</div>
	);
}

function SelectButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			className={`h-9 rounded-xl border text-sm transition-colors ${active ? 'border-primary/60 bg-primary/10 text-primary' : 'border-transparent bg-secondary/55 hover:bg-secondary'
				}`}
			onClick={onClick}
			type="button"
		>
			{children}
			{active && <Check className="ml-1 inline h-3 w-3" />}
		</button>
	);
}

function PayButton({ active, icon, label, onClick }: { active: boolean; icon: React.ReactNode; label: string; onClick: () => void }) {
	return (
		<button
			className={`flex h-10 items-center justify-center gap-2 rounded-xl border text-sm transition-colors ${active ? 'border-primary/60 bg-primary/10 text-primary' : 'border-transparent bg-secondary/55 hover:bg-secondary'
				}`}
			onClick={onClick}
			type="button"
		>
			{icon}
			{label}
			{active && <Check className="h-3 w-3" />}
		</button>
	);
}

function BalanceWarningModal({
	open,
	enabled,
	value,
	onClose,
	onConfirm,
}: {
	open: boolean;
	enabled: boolean;
	value: string;
	onClose: () => void;
	onConfirm: (enabled: boolean, value: string) => void;
}) {
	const [draftEnabled, setDraftEnabled] = useState(enabled);
	const [draftValue, setDraftValue] = useState(value);

	useEffect(() => {
		if (!open) return;
		setDraftEnabled(enabled);
		setDraftValue(value);
	}, [enabled, open, value]);

	if (!open) return null;

	const parsedValue = Number(draftValue);
	const valueValid = !draftEnabled || (Number.isInteger(parsedValue) && parsedValue >= 1 && parsedValue <= 10000);

	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center liquid-glass-overlay px-4">
			<div className="liquid-glass-modal w-full max-w-[620px] rounded-2xl p-6">
				<div className="flex items-center justify-between">
					<h2 className="text-xl font-semibold">设置余额预警</h2>
					<button aria-label="关闭" className="text-foreground/80 hover:text-foreground" onClick={onClose} type="button">
						<X className="h-6 w-6" />
					</button>
				</div>

				<div className="mt-7 flex gap-3 rounded-lg bg-primary/10 p-4 text-sm leading-7 text-foreground">
					<Info className="mt-1 h-5 w-5 shrink-0 text-primary" />
					<p>设置余额预警后，当账号余额低于预警阈值时，我们将向您的注册手机号/邮箱发送通知，请注意查收。</p>
				</div>

				<div className="mt-6 flex items-center justify-between gap-6">
					<span className="text-base font-medium">预警开关</span>
					<button
						aria-label="切换余额预警"
						className={`relative h-8 w-14 rounded-full transition-colors ${draftEnabled ? 'bg-primary' : 'bg-secondary'}`}
						onClick={() => setDraftEnabled(!draftEnabled)}
						type="button"
					>
						<span className={`absolute left-1 top-1 h-6 w-6 rounded-full bg-white transition-transform ${draftEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
					</button>
				</div>

				{draftEnabled && (
					<div className="mt-6 grid items-center gap-3 sm:grid-cols-[132px_1fr]">
						<span className="text-base font-medium">余额预警阈值</span>
						<div>
							<div className={`flex h-11 overflow-hidden rounded-lg border bg-background ${valueValid ? 'border-border/70' : 'border-red-400'}`}>
								<input
									className="min-w-0 flex-1 bg-transparent px-4 text-sm outline-none placeholder:text-muted-foreground/50"
									max={10000}
									min={1}
									onChange={(event) => {
										const raw = event.target.value;
										if (raw === '') {
											setDraftValue('');
											return;
										}
										const next = Math.trunc(Number(raw));
										if (!Number.isFinite(next) || next < 1) {
											setDraftValue('');
											return;
										}
										setDraftValue(String(Math.min(next, 10000)));
									}}
									placeholder="请输入 1 到 10000 之间的整数"
									step={1}
									type="number"
									value={draftValue}
								/>
								<span className="flex w-11 items-center justify-center border-l border-border/70 text-base">¥</span>
							</div>
							{!valueValid && <p className="mt-1 text-xs text-red-500">请输入 1 到 10000 之间的整数</p>}
						</div>
					</div>
				)}

				<div className="mt-7 grid gap-3 sm:grid-cols-2">
					<button className="h-11 rounded-full border border-border/70 text-base font-semibold hover:bg-secondary/60" onClick={onClose} type="button">
						取消
					</button>
					<button
						className="h-11 rounded-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
						disabled={!valueValid}
						onClick={() => onConfirm(draftEnabled, draftEnabled ? draftValue : '')}
						type="button"
					>
						确认
					</button>
				</div>
			</div>
		</div>
	);
}

function BillPanel() {
	const [billTab, setBillTab] = useState<BillTab>('usage');
	const { data: overview, isLoading: overviewLoading } = useBillOverview();

	const totalConsumed = overview ? formatFen(overview.totalConsumed) : '-';
	const formatToken = (v: number) => v.toLocaleString('zh-CN');

	if (overviewLoading) return <BillSkeleton />;

	return (
		<section className="liquid-glass-card no-hover rounded-2xl p-5">
			<div className="grid gap-3 md:grid-cols-[1fr_2fr]">
				<div className="rounded-xl bg-primary/10 p-4">
					<p className="text-xs">累计消费</p>
					<p className="mt-4 text-2xl font-semibold">¥ {totalConsumed}</p>
				</div>
				<div className="rounded-xl bg-secondary/45 p-4">
					<p className="border-l-2 border-primary pl-2 text-xs">Token 历史消耗</p>
					<div className="mt-4 grid gap-3 sm:grid-cols-4">
						<Metric label="总量" value={overview ? formatToken(overview.totalTokens) : '-'} />
						<Metric label="内容缓存" value={overview ? formatToken(overview.cachedTokens) : '-'} />
						<Metric label="未命中缓存" value={overview ? formatToken(overview.uncachedTokens) : '-'} />
						<Metric label="输出" value={overview ? formatToken(overview.outputTokens) : '-'} />
					</div>
				</div>
			</div>

			<div className="mt-5 flex border-b border-border/60 text-sm">
				<button
					className={`px-2 pb-3 ${billTab === 'usage' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
					onClick={() => setBillTab('usage')}
					type="button"
				>
					使用详情
				</button>
				<button
					className={`px-5 pb-3 ${billTab === 'monthly' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
					onClick={() => setBillTab('monthly')}
					type="button"
				>
					月度账单
				</button>
			</div>

			{billTab === 'usage' ? <UsageCharts /> : <MonthlyBillPanel />}
		</section>
	);
}

function Metric({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-xs text-muted-foreground">{label}</p>
			<p className="mt-1 text-lg font-semibold">{value}</p>
		</div>
	);
}

function UsageCharts() {
	// 查询当前时间到前 6 小时的数据，按小时粒度显示
	const endTs = Math.floor(Date.now() / 1000);
	const startTs = endTs - 6 * 3600;

	const { data: modelDataResp, isLoading } = useModelData(startTs, endTs, 'hour');
	const quotaData = modelDataResp?.items;

	// 将 NewapiQuotaDataItem[] 按小时聚合为三种图表数据
	const { hourlyQuota, hourlyCount, hourlyTokens, totalQuota, totalCount, totalTokens } = useMemo(() => {
		if (!quotaData || quotaData.length === 0) {
			return { hourlyQuota: [], hourlyCount: [], hourlyTokens: [], totalQuota: 0, totalCount: 0, totalTokens: 0 };
		}
		const quotaByHour = new Map<string, number>();
		const countByHour = new Map<string, number>();
		const tokensByHour = new Map<string, number>();
		let sumQuota = 0;
		let sumCount = 0;
		let sumTokens = 0;

		for (const item of quotaData) {
			// created_at 是按小时截断的 unix 秒，转为 HH:00 格式
			const d = new Date(item.created_at * 1000);
			const hourStr = `${String(d.getHours()).padStart(2, '0')}:00`;
			quotaByHour.set(hourStr, (quotaByHour.get(hourStr) ?? 0) + item.quota);
			countByHour.set(hourStr, (countByHour.get(hourStr) ?? 0) + item.count);
			tokensByHour.set(hourStr, (tokensByHour.get(hourStr) ?? 0) + item.token_used);
			sumQuota += item.quota;
			sumCount += item.count;
			sumTokens += item.token_used;
		}

		const hourlyQuota = Array.from(quotaByHour.entries())
			.map(([time, quota]) => ({ time, quota }))
			.sort((a, b) => a.time.localeCompare(b.time));
		const hourlyCount = Array.from(countByHour.entries())
			.map(([time, count]) => ({ time, count }))
			.sort((a, b) => a.time.localeCompare(b.time));
		const hourlyTokens = Array.from(tokensByHour.entries())
			.map(([time, tokens]) => ({ time, tokens }))
			.sort((a, b) => a.time.localeCompare(b.time));

		return { hourlyQuota, hourlyCount, hourlyTokens, totalQuota: sumQuota, totalCount: sumCount, totalTokens: sumTokens };
	}, [quotaData]);

	const totalQuotaDisplay = totalQuota.toLocaleString('zh-CN');
	const totalTokensDisplay = totalTokens.toLocaleString('zh-CN');
	const totalCountDisplay = totalCount.toLocaleString('zh-CN');
	const hasUsageRecords = Boolean(quotaData && quotaData.length > 0);

	return (
		<div className="mt-4 space-y-5">
			<InfoBar text="显示最近 6 小时的用量数据，按小时粒度统计，数据准实时更新（5 分钟以内延迟）。" />

			{isLoading ? (
				<div className="flex h-[260px] items-center justify-center rounded-xl border border-border/60 bg-card/60 text-sm text-muted-foreground">
					加载中...
				</div>
			) : !hasUsageRecords ? (
				<div className="flex h-[260px] items-center justify-center rounded-xl border border-dashed border-border/60 bg-card/40 text-sm text-muted-foreground">
					暂无记录
				</div>
			) : (
				<>

			{/* 消费账单 */}
			<div className="rounded-xl border border-border/60 bg-card/60 p-4">
				<h3 className="text-sm font-semibold">
					消费账单 <span className="ml-2 text-xs font-normal text-muted-foreground">{totalQuotaDisplay} 额度</span>
				</h3>
				<div className="mt-3 h-[260px]">
					<ResponsiveContainer height="100%" width="100%">
						<AreaChart data={hourlyQuota}>
							<CartesianGrid stroke="oklch(var(--border))" strokeDasharray="3 3" />
							<XAxis axisLine={false} dataKey="time" fontSize={11} tickLine={false} />
							<YAxis axisLine={false} fontSize={11} tickLine={false} />
							<Tooltip />
							<Legend />
							<Area dataKey="quota" fill="oklch(var(--primary))" fillOpacity={0.14} name="消耗额度" stroke="oklch(var(--primary))" type="monotone" />
						</AreaChart>
					</ResponsiveContainer>
				</div>
			</div>

			{/* Token 消耗 */}
			<div className="rounded-xl border border-border/60 bg-card/60 p-4">
				<h3 className="text-sm font-semibold">
					Token 消耗 <span className="ml-2 text-xs font-normal text-muted-foreground">{totalTokensDisplay} Tokens</span>
				</h3>
				<div className="mt-3 h-[260px]">
					<ResponsiveContainer height="100%" width="100%">
						<BarChart data={hourlyTokens}>
							<CartesianGrid stroke="oklch(var(--border))" strokeDasharray="3 3" />
							<XAxis axisLine={false} dataKey="time" fontSize={11} tickLine={false} />
							<YAxis axisLine={false} fontSize={11} tickLine={false} />
							<Tooltip />
							<Legend />
							<Bar dataKey="tokens" fill="oklch(var(--primary))" name="Token 消耗" radius={[4, 4, 0, 0]} />
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>

			{/* 请求次数 */}
			<div className="rounded-xl border border-border/60 bg-card/60 p-4">
				<h3 className="text-sm font-semibold">
					请求次数 <span className="ml-2 text-xs font-normal text-muted-foreground">{totalCountDisplay} 次</span>
				</h3>
				<div className="mt-3 h-[260px]">
					<ResponsiveContainer height="100%" width="100%">
						<LineChart data={hourlyCount}>
							<CartesianGrid stroke="oklch(var(--border))" strokeDasharray="3 3" />
							<XAxis axisLine={false} dataKey="time" fontSize={11} tickLine={false} />
							<YAxis axisLine={false} fontSize={11} tickLine={false} />
							<Tooltip />
							<Legend />
							<Line dataKey="count" dot={false} name="请求次数" stroke="oklch(var(--primary))" strokeWidth={2} type="monotone" />
						</LineChart>
					</ResponsiveContainer>
				</div>
			</div>
				</>
			)}
		</div>
	);
}

function MonthlyBillPanel() {
	// 默认当前月份
	const now = new Date();
	const [selectedYear, setSelectedYear] = useState(now.getFullYear());
	const [selectedMonth, setSelectedMonth] = useState(now.getMonth()); // 0-indexed

	// 根据选中年月计算时间戳范围
	const { startTs, endTs } = useMemo(() => {
		const start = new Date(selectedYear, selectedMonth, 1);
		const end = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59); // 月末最后一天
		return {
			startTs: Math.floor(start.getTime() / 1000),
			endTs: Math.floor(end.getTime() / 1000),
		};
	}, [selectedYear, selectedMonth]);

	const { data: modelDataResp, isLoading } = useModelData(startTs, endTs, 'day');
	const quotaData = modelDataResp?.items;

	// 按日期聚合：统计每日的请求次数、额度消耗、Token 消耗
	const dailyRows = useMemo(() => {
		if (!quotaData || quotaData.length === 0) return [];
		const byDate = new Map<string, { quota: number; count: number; tokenUsed: number }>();

		for (const item of quotaData) {
			const d = new Date(item.created_at * 1000);
			const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
			const existing = byDate.get(dateStr) ?? { quota: 0, count: 0, tokenUsed: 0 };
			existing.quota += item.quota;
			existing.count += item.count;
			existing.tokenUsed += item.token_used;
			byDate.set(dateStr, existing);
		}

		return Array.from(byDate.entries())
			.map(([date, data]) => ({ date, ...data }))
			.sort((a, b) => b.date.localeCompare(a.date)); // 按日期倒序
	}, [quotaData]);

	// 汇总
	const summary = useMemo(() => {
		let quota = 0;
		let count = 0;
		let tokenUsed = 0;
		for (const row of dailyRows) {
			quota += row.quota;
			count += row.count;
			tokenUsed += row.tokenUsed;
		}
		return { quota, count, tokenUsed };
	}, [dailyRows]);

	function handleMonthChange(year: number, month: number) {
		setSelectedYear(year);
		setSelectedMonth(month);
	}

	const monthLabel = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

	return (
		<div className="mt-4">
			<InfoBar text="每日数据将在次日 7:00（UTC 时间）完成最终核对，并以此作为费用结算依据。" />
			<div className="mt-4 flex justify-end gap-2">
				<MonthPicker label={monthLabel} month={selectedMonth} onChange={handleMonthChange} year={selectedYear} />
				<button
					className="flex h-9 items-center gap-1 rounded border border-border/70 px-4 text-xs text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
					type="button"
				>
					<Download className="h-3.5 w-3.5" />
					导出
				</button>
			</div>
			{/* 月度汇总 */}
			<div className="mt-3 grid gap-3 sm:grid-cols-3">
				<div className="rounded-xl bg-secondary/45 p-3">
					<p className="text-xs text-muted-foreground">月度额度消耗</p>
					<p className="mt-1 text-lg font-semibold">{summary.quota.toLocaleString('zh-CN')}</p>
				</div>
				<div className="rounded-xl bg-secondary/45 p-3">
					<p className="text-xs text-muted-foreground">月度请求次数</p>
					<p className="mt-1 text-lg font-semibold">{summary.count.toLocaleString('zh-CN')}</p>
				</div>
				<div className="rounded-xl bg-secondary/45 p-3">
					<p className="text-xs text-muted-foreground">月度 Token 消耗</p>
					<p className="mt-1 text-lg font-semibold">{summary.tokenUsed.toLocaleString('zh-CN')}</p>
				</div>
			</div>
			<div className="mt-3 overflow-hidden rounded-xl border border-border/60">
				<div className="grid grid-cols-4 bg-secondary/55 px-4 py-3 text-xs text-muted-foreground">
					<span>日期</span>
					<span>请求次数</span>
					<span>额度消耗</span>
					<span>Token 消耗</span>
				</div>
				{isLoading ? (
					<div className="px-4 py-8 text-center text-sm text-muted-foreground">加载中…</div>
				) : dailyRows.length === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-muted-foreground">暂无数据</div>
				) : (
					dailyRows.map((row) => (
						<div className="grid grid-cols-4 border-t border-border/60 px-4 py-3 text-sm" key={row.date}>
							<span>{row.date}</span>
							<span>{row.count.toLocaleString('zh-CN')}</span>
							<span>{row.quota.toLocaleString('zh-CN')}</span>
							<span>{row.tokenUsed.toLocaleString('zh-CN')}</span>
						</div>
					))
				)}
			</div>
		</div>
	);
}

/** 月份选择器：点击展开下拉选择年月 */
function MonthPicker({ year, month, onChange, label }: { year: number; month: number; onChange: (year: number, month: number) => void; label: string }) {
	const [open, setOpen] = useState(false);
	const [viewYear, setViewYear] = useState(year);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function handleClickOutside(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	const months = Array.from({ length: 12 }, (_, i) => i);

	return (
		<div className="relative" ref={ref}>
			<button
				className={`flex h-9 items-center gap-2 rounded border px-3 text-xs transition-colors ${open
					? 'border-primary/60 bg-primary/10 text-primary'
					: 'border-border/50 bg-secondary/40 text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
					}`}
				onClick={() => {
					setViewYear(year);
					setOpen((prev) => !prev);
				}}
				type="button"
			>
				{label}
				<CalendarDays className="h-3.5 w-3.5" />
			</button>
			{open && (
				<div className="absolute right-0 top-full z-10 mt-1 w-[280px] rounded-xl border border-border/50 bg-card p-3 shadow-xl backdrop-blur-xl">
					{/* 年份导航 */}
					<div className="mb-2 flex items-center justify-between">
						<button
							aria-label="上一年"
							className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
							onClick={() => setViewYear((y) => y - 1)}
							type="button"
						>
							<ChevronLeft className="h-3.5 w-3.5" />
						</button>
						<span className="text-sm font-medium">{viewYear} 年</span>
						<button
							aria-label="下一年"
							className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
							onClick={() => setViewYear((y) => y + 1)}
							type="button"
						>
							<ChevronRight className="h-3.5 w-3.5" />
						</button>
					</div>
					{/* 月份网格 */}
					<div className="grid grid-cols-4 gap-1">
						{months.map((m) => {
							const isActive = viewYear === year && m === month;
							return (
								<button
									className={`h-8 rounded-lg text-xs transition-colors ${isActive ? 'bg-primary font-medium text-primary-foreground' : 'text-foreground hover:bg-secondary/60'
										}`}
									key={m}
									onClick={() => {
										onChange(viewYear, m);
										setOpen(false);
									}}
									type="button"
								>
									{m + 1} 月
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}

function InfoBar({ text }: { text: string }) {
	return (
		<div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-xs text-foreground/80">
			<Info className="h-3.5 w-3.5" />
			{text}
		</div>
	);
}

/** 交易类型: 1=充值 2=赠送 3=消费 4=退款 */
const TRADE_TYPE_MAP: Record<number, string> = {
	1: '充值金额',
	2: '赠送金额',
	3: '消费',
	4: '退款',
};

const PAY_WAY_MAP: Record<string, string> = {
	WX_NATIVE: '微信',
	ALI_OC: '支付宝',
};

/** 交易状态: 0=失败 1=成功 2=处理中 */
function TradeStatusPill({ status }: { status: number }) {
	if (status === 1)
		return (
			<span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-primary">
				<Check className="h-3 w-3" />
				额度到账
			</span>
		);
	if (status === 2)
		return <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-amber-600 dark:text-amber-300">处理中</span>;
	return <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-red-500">失败</span>;
}

function RechargePanel() {
	const [showDateRange, setShowDateRange] = useState(false);
	// 日历中的预选日期（未确认）
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	// 实际用于查询的日期（点击确定后同步）
	const [queryStart, setQueryStart] = useState('');
	const [queryEnd, setQueryEnd] = useState('');
	const [page, setPage] = useState(1);
	const [typeFilter, setTypeFilter] = useState<0 | 1 | 2>(0);
	const calendarRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const pageSize = 20;

	const { data: summary, isLoading: summaryLoading } = useRechargeSummary();
	const { data: txData, isLoading: txLoading } = useTransactionList({
		page,
		pageSize,
		tradeType: typeFilter,
		startDate: queryStart || undefined,
		endDate: queryEnd || undefined,
	});

	const totalRecharged = summary ? formatFen(summary.totalRecharged) : '-';
	const rows = txData?.list ?? [];
	const total = txData?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / pageSize));

	// 点击外部关闭弹窗
	const handleClickOutside = useCallback(
		(e: MouseEvent) => {
			if (
				showDateRange &&
				calendarRef.current &&
				!calendarRef.current.contains(e.target as Node) &&
				triggerRef.current &&
				!triggerRef.current.contains(e.target as Node)
			) {
				setShowDateRange(false);
			}
		},
		[showDateRange],
	);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [handleClickOutside]);

	if (summaryLoading || txLoading) return <RechargeSkeleton />;

	// Reset page when filter changes
	function handleTypeChange(next: 0 | 1 | 2) {
		setTypeFilter(next);
		setPage(1);
	}

	function handleDateConfirm() {
		setQueryStart(startDate);
		setQueryEnd(endDate);
		setShowDateRange(false);
		setPage(1);
	}

	function handleDateClear() {
		setStartDate('');
		setEndDate('');
		setQueryStart('');
		setQueryEnd('');
		setShowDateRange(false);
		setPage(1);
	}

	return (
		<section className="liquid-glass-card no-hover relative space-y-4 rounded-2xl p-5">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<h2 className="text-base font-semibold">充值明细</h2>
				<div className="flex items-center gap-2">
					{/* 类型筛选 */}
					<div className="flex items-center gap-1 text-xs">
						{(
							[
								{ value: 0 as const, label: '全部' },
								{ value: 1 as const, label: '充值' },
								{ value: 2 as const, label: '赠送' },
							] as const
						).map((opt) => (
							<button
								className={`rounded-full px-2.5 py-1 transition-colors ${typeFilter === opt.value ? 'bg-primary/12 font-medium text-primary' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
									}`}
								key={opt.value}
								onClick={() => handleTypeChange(opt.value)}
								type="button"
							>
								{opt.label}
							</button>
						))}
					</div>
					{/* 日期范围 */}
					<div className="relative">
						<button
							className={`flex h-8 items-center gap-2 rounded-full border px-3 text-xs transition-colors ${showDateRange
								? 'border-primary/60 bg-primary/10 text-primary'
								: 'border-border/50 bg-secondary/40 text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
								}`}
							onClick={() => {
								if (!showDateRange) {
									// 打开时同步查询日期到预选
									setStartDate(queryStart);
									setEndDate(queryEnd);
								}
								setShowDateRange((open) => !open);
							}}
							ref={triggerRef}
							type="button"
						>
							<CalendarDays className="h-3.5 w-3.5 shrink-0" />
							<span className="tabular-nums">{queryStart || '开始日期'}</span>
							<span className="text-border/60">~</span>
							<span className="tabular-nums">{queryEnd || '结束日期'}</span>
						</button>
					</div>
				</div>
			</div>
			{showDateRange && (
				<div className="absolute right-5 top-[52px] z-10" ref={calendarRef}>
					{/* 气泡三角箭头：旋转 45° 正方形，下移覆盖面板上边框 */}
					<div
						className="absolute -top-1 right-8 z-20 h-3 w-3 rotate-45 border-l border-t border-border/50 bg-card"
						style={{ filter: 'drop-shadow(-2px -2px 3px rgba(0,0,0,0.06))' }}
					/>
					<DateRangeCalendar
						endDate={endDate}
						onClear={handleDateClear}
						onConfirm={handleDateConfirm}
						onEndChange={setEndDate}
						onStartChange={setStartDate}
						startDate={startDate}
					/>
				</div>
			)}
			<div className="rounded-xl bg-secondary/45 p-4">
				<p className="text-xs">累计充值</p>
				<p className="mt-4 text-2xl font-semibold">¥ {totalRecharged}</p>
			</div>
			<div className="overflow-x-auto">
				<div className="min-w-[800px] overflow-hidden rounded-xl border border-border/60">
					<div className="grid grid-cols-[1fr_1.8fr_0.8fr_0.8fr_0.8fr_0.8fr_1.8fr] items-center bg-secondary/55 px-4 py-3 text-xs text-muted-foreground">
						<span>交易时间</span>
						<span>订单编号</span>
						<span>交易金额</span>
						<span>交易类型</span>
						<span>支付方式</span>
						<span>交易状态</span>
						<span>备注</span>
					</div>
					{rows.map((row) => (
						<div className="grid grid-cols-[1fr_1.8fr_0.8fr_0.8fr_0.8fr_0.8fr_1.8fr] border-t border-border/60 px-4 py-3 text-xs" key={row.id}>
							<span>{row.createdAt}</span>
							<span>{row.orderNo}</span>
							<span>{formatFen(row.amount)}</span>
							<span>{TRADE_TYPE_MAP[row.tradeType] ?? '未知'}</span>
							<span>{row.payWay ? PAY_WAY_MAP[row.payWay] ?? row.payWay : '-'}</span>
							<span>
								<TradeStatusPill status={row.status} />
							</span>
							<span>{row.remark || row.expiresAt ? `${row.remark}${row.expiresAt ? ` 有效期至 ${row.expiresAt}` : ''}` : '-'}</span>
						</div>
					))}
				</div>
			</div>
			<Pagination
				onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
				onPrev={() => setPage((p) => Math.max(1, p - 1))}
				page={page}
				total={`共 ${total} 条`}
				totalPages={totalPages}
			/>
		</section>
	);
}

/** 双月日历日期范围选择器 */
function DateRangeCalendar({
	startDate,
	endDate,
	onStartChange,
	onEndChange,
	onConfirm,
	onClear,
}: {
	startDate: string;
	endDate: string;
	onStartChange: (v: string) => void;
	onEndChange: (v: string) => void;
	onConfirm: () => void;
	onClear: () => void;
}) {
	// 左侧月份，默认当月
	const [viewYear, setViewYear] = useState(() => {
		const d = startDate ? new Date(startDate) : new Date();
		return d.getFullYear();
	});
	const [viewMonth, setViewMonth] = useState(() => {
		const d = startDate ? new Date(startDate) : new Date();
		return d.getMonth(); // 0-indexed
	});

	const startMs = startDate ? new Date(startDate).getTime() : null;
	const endMs = endDate ? new Date(endDate).getTime() : null;

	function fmtDate(y: number, m: number, d: number) {
		return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
	}

	/** 点击日期 */
	function handleDayClick(dateStr: string) {
		const clickMs = new Date(dateStr).getTime();
		// 如果还没选开始日期，或已经选了完整的范围（重新开始选）
		if (!startMs || (startMs && endMs)) {
			onStartChange(dateStr);
			onEndChange('');
			return;
		}
		// 已有开始日期，在选结束日期
		if (clickMs < startMs) {
			onStartChange(dateStr);
			onEndChange(fmtDate(new Date(startMs).getFullYear(), new Date(startMs).getMonth(), new Date(startMs).getDate()));
		} else {
			onEndChange(dateStr);
		}
	}

	/** 导航 */
	function prevMonth() {
		if (viewMonth === 0) {
			setViewMonth(11);
			setViewYear((y) => y - 1);
		} else {
			setViewMonth((m) => m - 1);
		}
	}
	function nextMonth() {
		if (viewMonth === 11) {
			setViewMonth(0);
			setViewYear((y) => y + 1);
		} else {
			setViewMonth((m) => m + 1);
		}
	}
	function prevYear() {
		setViewYear((y) => y - 1);
	}
	function nextYear() {
		setViewYear((y) => y + 1);
	}

	const todayStr = fmtDate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

	const weekDays = ['一', '二', '三', '四', '五', '六', '日'];

	/** 生成某月的日历数据（含上月/下月补齐） */
	function buildCalendarGrid(year: number, month: number) {
		const firstDay = new Date(year, month, 1).getDay(); // 0=Sunday
		const offset = firstDay === 0 ? 6 : firstDay - 1; // 周一=0
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const prevMonthDays = new Date(year, month, 0).getDate();

		const cells: Array<{ day: number; dateStr: string; inMonth: boolean }> = [];

		// 上月补齐
		for (let i = offset - 1; i >= 0; i--) {
			const d = prevMonthDays - i;
			const pm = month === 0 ? 11 : month - 1;
			const py = month === 0 ? year - 1 : year;
			cells.push({ day: d, dateStr: fmtDate(py, pm, d), inMonth: false });
		}
		// 本月
		for (let d = 1; d <= daysInMonth; d++) {
			cells.push({ day: d, dateStr: fmtDate(year, month, d), inMonth: true });
		}
		// 下月补齐至 6 行 × 7 列 = 42
		const remain = 42 - cells.length;
		for (let d = 1; d <= remain; d++) {
			const nm = month === 11 ? 0 : month + 1;
			const ny = month === 11 ? year + 1 : year;
			cells.push({ day: d, dateStr: fmtDate(ny, nm, d), inMonth: false });
		}

		return cells;
	}

	function renderMonth(year: number, month: number) {
		const cells = buildCalendarGrid(year, month);
		return (
			<div className="flex-1 min-w-0">
				<div className="grid grid-cols-7 text-center">
					{weekDays.map((w) => (
						<span className="py-1.5 text-[11px] text-muted-foreground/60" key={w}>
							{w}
						</span>
					))}
				</div>
				<div className="grid grid-cols-7 gap-y-0.5">
					{cells.map((cell) => {
						const ms = new Date(cell.dateStr).getTime();
						const isStart = startMs === ms;
						const isEnd = endMs === ms;
						const inRange = startMs && endMs && ms > startMs && ms < endMs;
						const isToday = cell.dateStr === todayStr;

						return (
							<button
								className={`relative flex h-8 items-center justify-center rounded-lg text-xs transition-colors ${!cell.inMonth
									? 'text-muted-foreground/30'
									: isStart || isEnd
										? 'bg-primary font-semibold text-primary-foreground'
										: inRange
											? 'bg-primary/10 text-primary'
											: 'text-foreground hover:bg-secondary/60'
									}`}
								key={cell.dateStr}
								onClick={() => handleDayClick(cell.dateStr)}
								type="button"
							>
								{cell.day}
								{isToday && !isStart && !isEnd && <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-primary" />}
							</button>
						);
					})}
				</div>
			</div>
		);
	}

	const rightYear = viewMonth === 11 ? viewYear + 1 : viewYear;
	const rightMonth = viewMonth === 11 ? 0 : viewMonth + 1;

	return (
		<div
			className="w-[560px] origin-top rounded-2xl border border-border/50 bg-card p-4 shadow-xl backdrop-blur-xl"
			style={{ animation: 'calendar-slide-down 0.2s ease-out' }}
		>
			<div className="mb-2 flex items-center justify-between px-1">
				<div className="flex items-center gap-1">
					<button
						aria-label="上一年"
						className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
						onClick={prevYear}
						type="button"
					>
						<ChevronsLeft className="h-3.5 w-3.5" />
					</button>
					<button
						aria-label="上一月"
						className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
						onClick={prevMonth}
						type="button"
					>
						<ChevronLeft className="h-3.5 w-3.5" />
					</button>
				</div>
				<div className="flex gap-6 text-sm font-medium">
					<span>
						{viewYear} 年 {viewMonth + 1} 月
					</span>
					<span>
						{rightYear} 年 {rightMonth + 1} 月
					</span>
				</div>
				<div className="flex items-center gap-1">
					<button
						aria-label="下一月"
						className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
						onClick={nextMonth}
						type="button"
					>
						<ChevronRight className="h-3.5 w-3.5" />
					</button>
					<button
						aria-label="下一年"
						className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
						onClick={nextYear}
						type="button"
					>
						<ChevronsRight className="h-3.5 w-3.5" />
					</button>
				</div>
			</div>

			{/* 已选提示 */}
			{startMs && (
				<div className="mb-2 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
					<span className="tabular-nums">{startDate}</span>
					<span>~</span>
					<span className="tabular-nums">{endDate || '选择结束日期'}</span>
					{startMs && endMs && <span className="text-primary">{Math.round((endMs - startMs) / 86400000) + 1} 天</span>}
				</div>
			)}

			{/* 双月日历 */}
			<div className="grid grid-cols-2 gap-4">
				{renderMonth(viewYear, viewMonth)}
				{renderMonth(rightYear, rightMonth)}
			</div>

			{/* 底部操作 */}
			<div className="mt-3 grid grid-cols-2 gap-2">
				<button
					className="h-8 rounded-xl border border-border/50 text-xs text-muted-foreground transition-colors hover:bg-secondary/60"
					onClick={onClear}
					type="button"
				>
					清空
				</button>
				<button
					className="h-8 rounded-xl bg-primary text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
					onClick={onConfirm}
					type="button"
				>
					确定
				</button>
			</div>
		</div>
	);
}

function Pagination({ total, page, totalPages, onPrev, onNext }: { total: string; page?: number; totalPages?: number; onPrev?: () => void; onNext?: () => void }) {
	return (
		<div className="mt-4 flex items-center justify-end gap-2 text-xs">
			<span>{total}</span>
			<button
				className="flex h-7 w-7 items-center justify-center rounded border border-border/70 text-muted-foreground/50 disabled:opacity-40"
				disabled={page != null && page <= 1}
				onClick={onPrev}
				title="上一页"
				type="button"
			>
				<ChevronLeft className="h-3.5 w-3.5" />
			</button>
			<button className="h-7 w-7 rounded border border-primary text-primary" type="button">
				{page ?? 1}
			</button>
			<button
				className="flex h-7 w-7 items-center justify-center rounded border border-border/70 text-muted-foreground/50 disabled:opacity-40"
				disabled={page != null && totalPages != null && page >= totalPages}
				onClick={onNext}
				title="下一页"
				type="button"
			>
				<ChevronRight className="h-3.5 w-3.5" />
			</button>
		</div>
	);
}

/* ── Skeleton loaders — mirror each panel's layout ── */

function PulseBlock({ className }: { className?: string }) {
	return <div className={`animate-pulse rounded bg-secondary/50 ${className ?? ''}`} />;
}

function BalanceSkeleton() {
	return (
		<>
			<section className="grid gap-4 xl:grid-cols-2">
				<div className="liquid-glass-card no-hover rounded-2xl p-5">
					<div className="flex items-center justify-between gap-3">
						<div>
							<PulseBlock className="h-5 w-16" />
							<PulseBlock className="mt-2 h-3 w-64" />
						</div>
						<PulseBlock className="h-8 w-20 rounded-full" />
					</div>
					<div className="mt-5 rounded-xl bg-secondary/45 p-4">
						<PulseBlock className="h-3 w-16" />
						<PulseBlock className="mt-3 h-9 w-40" />
						<PulseBlock className="mt-4 h-3 w-56" />
					</div>
				</div>
				<div className="liquid-glass-card no-hover rounded-2xl p-5">
					<div className="flex items-start justify-between gap-3">
						<div>
							<PulseBlock className="h-5 w-20" />
							<PulseBlock className="mt-2 h-3 w-48" />
						</div>
						<PulseBlock className="h-8 w-16 rounded-full" />
					</div>
					<div className="mt-5 rounded-xl bg-secondary/45 p-4">
						<PulseBlock className="h-3 w-16" />
						<PulseBlock className="mt-3 h-9 w-28" />
						<PulseBlock className="mt-4 h-3 w-32" />
					</div>
				</div>
			</section>

			<section className="liquid-glass-card no-hover rounded-2xl p-6">
				<PulseBlock className="mx-auto h-6 w-24" />
				<div className="mx-auto mt-5 max-w-3xl">
					<PulseBlock className="h-3 w-16" />
					<div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
						<PulseBlock className="h-9 rounded-xl" />
						<PulseBlock className="h-9 rounded-xl" />
						<PulseBlock className="h-9 rounded-xl" />
						<PulseBlock className="h-9 rounded-xl" />
						<PulseBlock className="h-9 rounded-xl" />
						<PulseBlock className="h-9 rounded-xl" />
						<PulseBlock className="h-9 rounded-xl" />
						<PulseBlock className="h-9 rounded-xl" />
					</div>
					<PulseBlock className="mt-5 h-3 w-16" />
					<div className="mt-2 grid gap-2 sm:grid-cols-2">
						<PulseBlock className="h-10 rounded-xl" />
						<PulseBlock className="h-10 rounded-xl" />
					</div>
					<PulseBlock className="mt-4 h-3 w-48" />
					<PulseBlock className="mt-4 h-10 w-full rounded-full" />
				</div>
			</section>
		</>
	);
}

function BillSkeleton() {
	return (
		<section className="liquid-glass-card no-hover rounded-2xl p-5">
			<div className="grid gap-3 md:grid-cols-[1fr_2fr]">
				<div className="rounded-xl bg-primary/10 p-4">
					<PulseBlock className="h-3 w-16" />
					<PulseBlock className="mt-4 h-8 w-28" />
				</div>
				<div className="rounded-xl bg-secondary/45 p-4">
					<PulseBlock className="h-3 w-28" />
					<div className="mt-4 grid gap-3 sm:grid-cols-4">
						<div>
							<PulseBlock className="h-3 w-12" />
							<PulseBlock className="mt-1.5 h-6 w-16" />
						</div>
						<div>
							<PulseBlock className="h-3 w-12" />
							<PulseBlock className="mt-1.5 h-6 w-16" />
						</div>
						<div>
							<PulseBlock className="h-3 w-12" />
							<PulseBlock className="mt-1.5 h-6 w-16" />
						</div>
						<div>
							<PulseBlock className="h-3 w-12" />
							<PulseBlock className="mt-1.5 h-6 w-16" />
						</div>
					</div>
				</div>
			</div>

			<div className="mt-5 flex gap-4 border-b border-border/60 pb-3">
				<PulseBlock className="h-4 w-16" />
				<PulseBlock className="h-4 w-16" />
			</div>

			<div className="mt-4 space-y-5">
				<div className="flex justify-end gap-2">
					<PulseBlock className="h-8 w-36 rounded" />
					<PulseBlock className="h-8 w-24 rounded" />
				</div>
				<div className="rounded-xl border border-border/60 bg-card/60 p-4">
					<PulseBlock className="h-4 w-28" />
					<div className="mt-3 h-[260px] rounded-lg bg-secondary/30" />
				</div>
				<div className="rounded-xl border border-border/60 bg-card/60 p-4">
					<PulseBlock className="h-4 w-24" />
					<div className="mt-3 h-[260px] rounded-lg bg-secondary/30" />
				</div>
			</div>
		</section>
	);
}

function SkeletonTableRows() {
	const rowClass = 'grid grid-cols-[1fr_1.8fr_0.8fr_0.8fr_0.8fr_0.8fr_1.8fr] border-t border-border/60 px-4 py-3';
	return (
		<>
			<div className={rowClass}>
				<PulseBlock className="h-3 w-20" />
				<PulseBlock className="h-3 w-28" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-12" />
				<PulseBlock className="h-5 w-16 rounded-full" />
				<PulseBlock className="h-3 w-24" />
			</div>
			<div className={rowClass}>
				<PulseBlock className="h-3 w-20" />
				<PulseBlock className="h-3 w-28" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-12" />
				<PulseBlock className="h-5 w-16 rounded-full" />
				<PulseBlock className="h-3 w-24" />
			</div>
			<div className={rowClass}>
				<PulseBlock className="h-3 w-20" />
				<PulseBlock className="h-3 w-28" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-12" />
				<PulseBlock className="h-5 w-16 rounded-full" />
				<PulseBlock className="h-3 w-24" />
			</div>
			<div className={rowClass}>
				<PulseBlock className="h-3 w-20" />
				<PulseBlock className="h-3 w-28" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-12" />
				<PulseBlock className="h-5 w-16 rounded-full" />
				<PulseBlock className="h-3 w-24" />
			</div>
			<div className={rowClass}>
				<PulseBlock className="h-3 w-20" />
				<PulseBlock className="h-3 w-28" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-3 w-12" />
				<PulseBlock className="h-5 w-16 rounded-full" />
				<PulseBlock className="h-3 w-24" />
			</div>
		</>
	);
}

function RechargeSkeleton() {
	return (
		<section className="liquid-glass-card no-hover relative space-y-4 rounded-2xl p-5">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<PulseBlock className="h-5 w-20" />
				<div className="flex items-center gap-2">
					<div className="flex items-center gap-1">
						<PulseBlock className="h-6 w-12 rounded-full" />
						<PulseBlock className="h-6 w-12 rounded-full" />
						<PulseBlock className="h-6 w-12 rounded-full" />
					</div>
					<PulseBlock className="h-8 w-44 rounded-full" />
				</div>
			</div>
			<div className="rounded-xl bg-secondary/45 p-4">
				<PulseBlock className="h-3 w-16" />
				<PulseBlock className="mt-4 h-7 w-28" />
			</div>
			<div className="overflow-hidden rounded-xl border border-border/60">
				<div className="grid grid-cols-[1fr_1.8fr_0.8fr_0.8fr_0.8fr_0.8fr_1.8fr] items-center bg-secondary/55 px-4 py-3">
					<PulseBlock className="h-3 w-12" />
					<PulseBlock className="h-3 w-12" />
					<PulseBlock className="h-3 w-12" />
					<PulseBlock className="h-3 w-12" />
					<PulseBlock className="h-3 w-12" />
					<PulseBlock className="h-3 w-12" />
					<PulseBlock className="h-3 w-12" />
				</div>
				<SkeletonTableRows />
			</div>
			<div className="mt-4 flex items-center justify-end gap-2">
				<PulseBlock className="h-3 w-14" />
				<PulseBlock className="h-7 w-7 rounded" />
				<PulseBlock className="h-7 w-7 rounded" />
				<PulseBlock className="h-7 w-7 rounded" />
			</div>
		</section>
	);
}
